/** Huddle pieces of the window chrome: the title-bar pill and the settings sheet (one per window). */
import { Shell, Sidebar, type OpenTarget } from "@bundle/core";
import { HuddlePill, NewMeetingSheet, useFollow, useHuddle, useWorkspace } from "@bundle/ui";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { HuddlesDialogs } from "./HuddlesDialogs.tsx";

export function TitlebarHuddle({ open, canSplit }: { open: (url: string, target?: OpenTarget) => void; canSplit: boolean }) {
  const huddle = useHuddle();
  const ws = useWorkspace();
  if (huddle === null) return null;
  const { vm, state } = huddle;
  const channelId = state.call.channelId;
  if (state.call.status === "idle" || channelId === undefined) return null;
  return (
    <HuddlePill
      title={state.title}
      isDm={state.isDm}
      status={state.call.status}
      count={state.tiles.length}
      speaking={state.speaking.length > 0}
      muted={state.me.muted}
      onOpen={() => open(Shell.formatUrl({ type: "huddle", ws: ws.slug, huddleId: channelId }), canSplit ? "split" : "current")}
      onMute={() => vm.toggleMute()}
      onLeave={() => void vm.leave()}
      testID="huddle-pill"
    />
  );
}

/** Opens a URL in the shell (the window's chrome sits outside the panes' navigation). */
type Open = (url: string, target?: OpenTarget) => void;

const SettingsSheet = lazy(() => import("./HuddleSettings.tsx").then((m) => ({ default: m.HuddleSettingsPanel })));

/**
 * The settings sheet, loaded the first time it opens; "New external huddle" when it's open (in a workspace); and the
 * Huddles area's dialogs.
 */
export function HuddleSettings({ open }: { open?: Open }) {
  const huddle = useHuddle();
  const [opened, setOpened] = useState(false);
  // Load the sheet's chunk on first open, then keep it mounted (its close animation and state survive).
  if (huddle?.state.settingsOpen === true && !opened) setOpened(true);
  if (huddle === null) return null;
  return (
    <>
      {opened ? (
        <Suspense fallback={null}>
          <SettingsSheet />
        </Suspense>
      ) : null}
      {open === undefined ? null : (
        <>
          {huddle.state.newMeeting === undefined ? null : <NewMeeting open={open} />}
          <HuddlesDialogs open={open} />
        </>
      )}
    </>
  );
}

const COPIED_MS = 2000;

/** "New external huddle" (opened from the workspace menu or a conversation's huddle pane). */
function NewMeeting({ open }: { open: Open }) {
  const huddle = useHuddle();
  const ws = useWorkspace();
  const follow = useFollow(open);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(id);
  }, [copied]);
  const cache = ws.state.cache;
  const conversations = useMemo(() => {
    if (cache === undefined) return [];
    return Object.keys(cache.memberships)
      .map((id) => cache.channels[id])
      .filter((c) => c !== undefined && !c.archived)
      .map((c) => {
        const title = Sidebar.channelTitle(cache, c!.id);
        return { id: c!.id, label: c!.kind === "dm" || c!.kind === "group_dm" ? title : `# ${title}` };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cache]);
  if (huddle === null) return null;
  const { vm, state } = huddle;
  return (
    <NewMeetingSheet
      form={state.newMeeting}
      conversations={conversations}
      canCreate={vm.canCreateMeeting()}
      copied={copied}
      onEdit={(patch) => vm.editNewMeeting(patch)}
      onCreate={() => void vm.createMeeting()}
      onCopy={(url) => void navigator.clipboard.writeText(url).then(() => setCopied(true), () => undefined)}
      onStart={() => {
        // The call runs in the app, under the conversation or the link's own huddle (the system browser on Linux).
        const key = vm.startMeeting(!huddle.browserOnly);
        if (key !== undefined) follow({ kind: "call", channelKey: key });
      }}
      onClose={() => vm.closeNewMeeting()}
      testID="new-meeting"
    />
  );
}
